import React, { useState } from 'react';
import { Row, Col } from 'antd';
import { I18n } from '@aws-amplify/core';
import { DownloadOutlined, LockOutlined, UnlockOutlined } from '@ant-design/icons';

import { ModalPdf } from '../../../PdfModal';
import { usePDFGenerate } from './hooks/usePDFGenerate';
import { useResolution886Report } from '@/context/Resolution886Report';
import { useApplicationContext } from '@/context/v1/Application/context';
import { PDFSectionsResolution886 } from '@/components/views/Resolution886/enums';

import * as S from './styles';

interface PDFModalValues {
	pdf_custom: PDFSectionsResolution886[];
}

interface ConsolidatePDFValues {
	status: boolean;
	include_fields?: PDFSectionsResolution886[];
}

interface ButtonsCustomProps {
	isLoadingPDF: boolean;
	consolidated: boolean;
	hasCustomModal: boolean;
	hasConsolidated: boolean;
	onConsolidatePDF(values: ConsolidatePDFValues): void;
}

export function ButtonsCustom({
	isLoadingPDF = false,
	consolidated = false,
	hasConsolidated = false,
	onConsolidatePDF
}: Readonly<ButtonsCustomProps>) {
	const [isOpenModalPDF, setIsOpenModalPDF] = useState<boolean>(false);
	const [isConsolidateMode, setIsConsolidateMode] = useState<boolean>(false);

	const { organization, company } = useApplicationContext();
	const { report } = useResolution886Report();

	const { onDownloadPDF, downloadPDFLoading } = usePDFGenerate({
		report,
		organizationId: organization?.id!,
		companyId: company?.id!
	});

	function handleOpenModal(): void {
		handleOnSubmit();
	}

	function handleCloseModal(): void {
		setIsOpenModalPDF(false);
		setIsConsolidateMode(false);
	}

	async function handleOnSubmit(): Promise<void> {
		await onDownloadPDF();
		handleCloseModal();
	}

	async function handleConsolidateWithPDF(values: PDFModalValues): Promise<void> {
		await onConsolidatePDF({
			status: !consolidated,
			include_fields: values.pdf_custom || []
		});
		handleCloseModal();
	}

	async function handleConsolidate(): Promise<void> {
		setIsConsolidateMode(true);
		setIsOpenModalPDF(true);
	}

	return (
		<Row justify="end" align="middle" gutter={[30, 0]}>
			<Col>
				<S.DownloadButton
					ghost
					size="large"
					type="primary"
					loading={downloadPDFLoading}
					icon={<DownloadOutlined />}
					onClick={handleOpenModal}
				>
					{I18n.get('Download PDF')}
				</S.DownloadButton>
			</Col>
			{hasConsolidated && (
				<Col>
					<S.ConsolidateButton
						$isConsolidated={!consolidated}
						ghost
						size="large"
						type="primary"
						loading={isLoadingPDF}
						icon={!consolidated ? <LockOutlined style={{ color: '#2cc852' }} /> : <UnlockOutlined />}
						onClick={handleConsolidate}
					>
						{!consolidated ? I18n.get('Consolidate report') : I18n.get('Deconsolidate')}
					</S.ConsolidateButton>
				</Col>
			)}
			<ModalPdf
				open={isOpenModalPDF}
				loading={downloadPDFLoading}
				onClose={handleCloseModal}
				onSubmit={isConsolidateMode ? handleConsolidateWithPDF : handleOnSubmit}
				onShowFieldSelection
			/>
		</Row>
	);
}
